import { OrderPixCharge, OrderResponse } from "@/types/external"
import { OrderStatusType } from "@/types/order-status"
import { translateErrorCode } from "@/lib/i18n/error-messages"
import { formatScheduledFor } from "@/lib/scheduling/format"
import { formatBRL } from "@/lib/format-brl"
import { format } from "date-fns"

/**
 * Statuses that indicate the order lifecycle is over from the customer's
 * point of view. Anything NOT in this set is considered "in progress".
 */
export const TERMINAL_STATUSES: ReadonlySet<OrderStatusType> = new Set<OrderStatusType>([
  "DELIVERED",
  "PICKED_UP",
  "CONCLUDED",
  "CANCELLED",
  "CANCELLED_DENIED",
])

/**
 * Returns the most recent status from the `events` array, or null if the
 * order has no events yet.
 */
export function getCurrentStatus(order: OrderResponse | null | undefined): OrderStatusType | null {
  if (!order?.events || order.events.length === 0) return null
  const last = order.events[order.events.length - 1]
  return (last?.status as OrderStatusType) ?? null
}

/**
 * True when the order is still in progress (not in a terminal state).
 * An order with no events yet is treated as in progress (it was just placed).
 */
export function isOrderInProgress(order: OrderResponse | null | undefined): boolean {
  if (!order) return false
  const status = getCurrentStatus(order)
  if (!status) return true
  return !TERMINAL_STATUSES.has(status)
}

/**
 * True when the order is a PIX Online order still awaiting payment.
 *
 * The backend encodes PIX Online as method="PIX" + methodInfo="PIX_ONLINE"
 * (NOT method="PIX_ONLINE"). The methodInfo marker persists even after payment
 * confirms, so we MUST also gate on the status: the order sits in CREATING
 * until the `payment.confirmed` event drives it to CREATED.
 *
 * Drives the "Aguardando pagamento" label and the "Exibir QR Code" action.
 */
export function isAwaitingPixPayment(order: OrderResponse | null | undefined): boolean {
  if (!order) return false
  const firstMethod = order.payments?.methods?.[0]
  if (firstMethod?.method !== "PIX" || firstMethod?.methodInfo !== "PIX_ONLINE") return false
  const status = getCurrentStatus(order)
  return status === null || status === "CREATING"
}

/**
 * Ad-hoc Pix charge statuses (PIX-CHARGE, D5) that still require customer
 * action, the merchant is waiting for this money.
 */
const OPEN_PIX_CHARGE_STATUSES: ReadonlySet<OrderPixCharge["status"]> = new Set([
  "PENDING",
  "AWAITING_PAYMENT",
])

/**
 * Open ad-hoc Pix charges (PIX-CHARGE) on this order, i.e. charges the
 * customer still needs to pay. Distinct from {@link isAwaitingPixPayment},
 * which gates the ORIGINAL checkout payment (order.payments.methods,
 * status CREATING) and must not be touched by this feature: an ad-hoc
 * charge can exist on an order in any non-terminal status (e.g. PREPARING,
 * after a mid-order edit added a balance due).
 *
 * Purely derived from `order.openCharges` as returned by the 10s poll, so
 * a charge going PAID or EXPIRED on the next poll naturally drops out here
 *, no extra removal logic needed.
 */
export function getOpenPixCharges(
  order: OrderResponse | null | undefined,
): OrderPixCharge[] {
  const charges = order?.openCharges ?? []
  const now = Date.now()
  return charges.filter((charge) => {
    if (!OPEN_PIX_CHARGE_STATUSES.has(charge.status)) return false
    if (charge.expiresAt) {
      const exp = new Date(charge.expiresAt).getTime()
      if (!Number.isNaN(exp) && exp <= now) return false
    }
    return true
  })
}

/**
 * True when the order has at least one open (PENDING/AWAITING_PAYMENT)
 * ad-hoc Pix charge, drives the "balance due" card on the order detail
 * screen (PIX-CHARGE D6).
 */
export function hasOpenPixCharge(order: OrderResponse | null | undefined): boolean {
  return getOpenPixCharges(order).length > 0
}

/**
 * The most recent open ad-hoc Pix charge, i.e. the one the "balance due"
 * card should offer to pay. Charges are appended by the backend in
 * creation order, so the last open one is the most recent.
 */
export function getLatestOpenPixCharge(
  order: OrderResponse | null | undefined,
): OrderPixCharge | null {
  const open = getOpenPixCharges(order)
  return open.length > 0 ? open[open.length - 1] : null
}

/** True when the order was placed for a future window. */
export function isScheduledOrder(order: OrderResponse | null | undefined): boolean {
  if (!order) return false
  return order.orderTiming === "SCHEDULED" || order.Timing === "SCHEDULED"
}

/** The order's `readyAt` (leave-the-store / pickup instant); null when immediate. */
export function getScheduledReadyAt(order: OrderResponse | null | undefined): string | null {
  if (!isScheduledOrder(order)) return null
  const start = order?.schedule?.scheduledDateTimeStart
  return start && !Number.isNaN(new Date(start).getTime()) ? start : null
}

/**
 * Lead time used as the cancellation cut-off when the read payload does not
 * carry `preparationStartDateTime` yet (older customer-service-gateway builds).
 * Deliberately conservative: it closes the button EARLIER than the backend
 * would, so the degraded UI never promises a cancellation the RPC refuses.
 */
export const SCHEDULED_CANCEL_FALLBACK_LEAD_MINUTES = 60

/**
 * When the kitchen starts a scheduled order. Prefers the backend's
 * `preparationStartDateTime`; degrades to `scheduledDateTimeStart` minus
 * {@link SCHEDULED_CANCEL_FALLBACK_LEAD_MINUTES}. Null on immediate orders and
 * on scheduled ones with no usable ready window.
 */
export function getPreparationStartAt(order: OrderResponse | null | undefined): Date | null {
  if (!isScheduledOrder(order)) return null
  const declared = order?.preparationStartDateTime
  if (declared) {
    const parsed = new Date(declared)
    if (!Number.isNaN(parsed.getTime())) return parsed
  }
  const readyAt = getScheduledReadyAt(order)
  if (!readyAt) return null
  return new Date(
    new Date(readyAt).getTime() - SCHEDULED_CANCEL_FALLBACK_LEAD_MINUTES * 60_000,
  )
}

/**
 * True when the read payload did not carry `preparationStartDateTime` and the
 * cut-off above was derived. Drives the extra sentence in the dialog.
 */
export function isPreparationStartDegraded(
  order: OrderResponse | null | undefined,
): boolean {
  if (!isScheduledOrder(order)) return false
  const declared = order?.preparationStartDateTime
  if (!declared) return getScheduledReadyAt(order) !== null
  return Number.isNaN(new Date(declared).getTime())
}

/**
 * Statuses in which a scheduled order can still be cancelled by the customer.
 * `PREPARATION_REQUESTED` is already the kitchen being told to start, so it is
 * out along with `PREPARING` and everything after it.
 */
const SCHEDULED_CANCELLABLE_STATUSES: ReadonlySet<OrderStatusType> = new Set<OrderStatusType>([
  "CREATED",
  "CONFIRMED",
])

const PREPARATION_STARTED_STATUSES: ReadonlySet<string> = new Set([
  "PREPARATION_REQUESTED",
  "PREPARING",
])

/**
 * Mirror of order-service-core's `CancelScheduledOrderByCustomer` guard
 * (ORD-ORD-1014): scheduled, not terminal, kitchen not started, and still
 * before the preparation cut-off. The backend stays the authority, this only
 * decides whether the button is worth showing.
 */
export function canCancelScheduledOrder(
  order: OrderResponse | null | undefined,
  now: Date = new Date(),
): boolean {
  if (!isScheduledOrder(order)) return false
  const status = getCurrentStatus(order)
  if (!status || !SCHEDULED_CANCELLABLE_STATUSES.has(status)) return false
  if (TERMINAL_STATUSES.has(status)) return false
  if ((order?.events ?? []).some((event) => PREPARATION_STARTED_STATUSES.has(event.status))) {
    return false
  }
  const preparationStart = getPreparationStartAt(order)
  if (!preparationStart) return false
  return now.getTime() < preparationStart.getTime()
}

/**
 * Statuses of a scheduled order in which nothing is happening in the kitchen
 * yet: the store accepted it and will start cooking at its own time. Saying
 * "estamos preparando" here would be a lie the customer can check.
 */
const SCHEDULED_WAITING_STATUSES: ReadonlySet<OrderStatusType> = new Set<OrderStatusType>([
  "CREATING",
  "CREATED",
  "CONFIRMED",
])

/**
 * Human-friendly status label used in the active-order bar and order detail.
 * Keep in sync with OrderStatusType.
 */
export function getStatusLabel(
  status: OrderStatusType | null | undefined,
  orderType?: string,
): string {
  if (orderType === "INDOOR") {
    if (status === "READY_FOR_PICKUP") return "Pedido pronto, deve ser entregue em breve..."
    if (status === "PICKED_UP") return "Pedido entregue"
  }
  switch (status) {
    case "CREATING":
      return "Realizando o pedido"
    case "CREATED":
      return "Aguardando confirmação da loja"
    case "CONFIRMED":
      return "Pedido confirmado pela loja"
    case "PREPARATION_REQUESTED":
      return "Preparo do pedido solicitado"
    case "PREPARING":
      return "Estamos preparando seu pedido"
    case "READY_FOR_PICKUP":
      if (orderType === "DELIVERY") return "Pedido pronto"
      return "Pronto para retirada"
    case "PICKUP_AREA_ASSIGNED":
      return "Área de retirada definida"
    case "DISPATCHED":
      return "Pedido saiu para entrega"
    case "DELIVERED":
      return "Pedido entregue"
    case "PICKED_UP":
      return "Pedido retirado"
    case "CONCLUDED":
      return "Pedido concluído"
    case "CANCELLATION_REQUESTED":
      return "Cancelamento solicitado"
    case "CANCELLATION_REQUEST_DENIED":
      return "Cancelamento negado"
    case "ORDER_CANCELLATION_REQUEST":
      return "Loja solicitou cancelamento"
    case "CANCELLED_DENIED":
      return "Cancelamento negado"
    case "REFUND_PROCESSING":
      return "Cancelamento aprovado, estorno Pix em processamento"
    case "CANCELLED":
      return "Pedido cancelado"
    default:
      return "Aguardando confirmação do pedido"
  }
}

/**
 * Refunds that already settled (order-service-core / customer-service-
 * gateway never surface a row before it succeeds, see order-response.ts).
 */
function getSettledRefunds(
  order: OrderResponse | null | undefined,
): NonNullable<OrderResponse["payments"]["refunds"]> {
  return order?.payments?.refunds ?? []
}

/**
 * Sum of settled refunds (major currency unit, same as payments.pending).
 * 0 when the order has no refund.
 */
export function getTotalRefundedAmount(order: OrderResponse | null | undefined): number {
  const refunds = getSettledRefunds(order)
  if (refunds.length === 0) return order?.payments?.refunded ?? 0
  return refunds.reduce((sum, r) => sum + (r.value ?? 0), 0)
}

/**
 * True when the order carries at least one settled refund line.
 */
export function hasSettledRefund(order: OrderResponse | null | undefined): boolean {
  return getTotalRefundedAmount(order) > 0
}

/**
 * Resolves a customer-facing cancellation reason from a STABLE reason code.
 * Returns null when unknown; callers keep their generic cancelled-order copy.
 * Uses `translateErrorCode` (not category fallback). See backendDependency for
 * async checkout code propagation gaps.
 */
export function getCancellationReasonMessage(
  order: OrderResponse | null | undefined,
): string | null {
  if (!order) return null
  const cancelledEvent = [...(order.events ?? [])]
    .reverse()
    .find((event) => event.status === "CANCELLED")
  const code = order.cancellationReasonCode ?? cancelledEvent?.code
  return translateErrorCode(code)
}

const DENIED_CANCELLATION_STATUSES: ReadonlySet<string> = new Set([
  "CANCELLATION_REQUEST_DENIED",
  "CANCELLED_DENIED",
])

/**
 * True when the order history includes a merchant denial of a cancellation
 * request, even if later events advanced the lifecycle past that denial.
 */
export function hasCancellationDenied(
  order: OrderResponse | null | undefined,
): boolean {
  if (!order) return false
  return (order.events ?? []).some((e) => DENIED_CANCELLATION_STATUSES.has(e.status))
}

/**
 * Merchant free-text explaining why the cancellation was denied
 * (cancellation.metadata.cancellationReply). Null when there is no denial,
 * or the store left no explanation (no generic fallback).
 */
export function getCancellationDeniedReason(
  order: OrderResponse | null | undefined,
): string | null {
  if (!order || !hasCancellationDenied(order)) return null
  const meta = order.cancellation?.metadata as
    | {
        cancellationReply?: string
        merchantMessage?: string
        customerMessage?: string
        cancellationReason?: string
      }
    | undefined
  const fromReply = meta?.cancellationReply?.trim()
  if (fromReply) return fromReply
  const fromLegacyMerchant = meta?.merchantMessage?.trim()
  if (fromLegacyMerchant) return fromLegacyMerchant
  return null
}

/**
 * Customer-facing text when a cancellation request was denied by the store.
 * Prefer {@link getCancellationDeniedReason} when you only want free text;
 * this adds a generic fallback for contexts that always need a sentence.
 */
export function getCancellationDeniedMessage(
  order: OrderResponse | null | undefined,
): string | null {
  if (!order || !hasCancellationDenied(order)) return null
  return (
    getCancellationDeniedReason(order) ??
    "A loja não pôde cancelar o pedido. Se precisar, entre em contato com o estabelecimento."
  )
}

/**
 * Progress percentage (0–100) for the status bar. Mirrors the existing
 * ruler used in StatusOrderClient so the visual behavior stays consistent.
 */
export function getProgressPercent(status: OrderStatusType | null | undefined): number {
  switch (status) {
    case "CREATING":
      return 16
    case "CREATED":
      return 20
    case "CONFIRMED":
      return 25
    case "PREPARATION_REQUESTED":
      return 33
    case "PREPARING":
      return 40
    case "READY_FOR_PICKUP":
      return 60
    case "PICKUP_AREA_ASSIGNED":
      return 60
    case "DISPATCHED":
      return 80
    case "DELIVERED":
    case "PICKED_UP":
    case "CONCLUDED":
    case "CANCELLATION_REQUESTED":
    case "CANCELLATION_REQUEST_DENIED":
    case "ORDER_CANCELLATION_REQUEST":
    case "CANCELLED_DENIED":
    case "REFUND_PROCESSING":
    case "CANCELLED":
      return 100
    default:
      return 16
  }
}

/** Semantic tone of an order's progress (bar fill, status badge): happy path, cancellation in flux, cancelled. */
export type OrderProgressTone = "secondary" | "warning" | "danger"

/**
 * Tone of the progress bar and status badge: `warning` while a cancellation is
 * pending, denied or refunding, `danger` once cancelled, `secondary` otherwise.
 */
export function getProgressTone(status: OrderStatusType | null | undefined): OrderProgressTone {
  switch (status) {
    case "CANCELLATION_REQUESTED":
    case "CANCELLATION_REQUEST_DENIED":
    case "ORDER_CANCELLATION_REQUEST":
    case "CANCELLED_DENIED":
    case "REFUND_PROCESSING":
      return "warning"
    case "CANCELLED":
      return "danger"
    default:
      return "secondary"
  }
}

/**
 * Statuses in which the customer may open “estou com um problema” and
 * request cancellation, from placement (awaiting store accept) through
 * preparation. Later stages (ready / dispatched / terminal) are blocked.
 */
const CANCELLATION_REQUEST_STATUSES: ReadonlySet<OrderStatusType> = new Set([
  "CREATED",
  "CONFIRMED",
  "PREPARATION_REQUESTED",
  "PREPARING",
])

/**
 * True when the order is still in an early lifecycle stage (created through
 * preparation) AND the customer has not yet used their one cancellation
 * attempt (no CANCELLATION_REQUESTED in history).
 */
export function canRequestCancellation(
  order: OrderResponse | null | undefined,
): boolean {
  if (!order) return false
  const status = getCurrentStatus(order)
  if (!status || !CANCELLATION_REQUEST_STATUSES.has(status)) return false
  const alreadyRequested = (order.events ?? []).some(
    (e) => e.status === "CANCELLATION_REQUESTED",
  )
  return !alreadyRequested
}

export const MIN_CANCELLATION_REASON_LEN = 10

/**
 * Formats the estimated arrival (delivery or takeout) as "HH:mm" or a
 * finalized label when the order is already delivered/picked up.
 */
export function formatEstimatedArrival(order: OrderResponse): string {
  const currentStatus = getCurrentStatus(order)

  if (currentStatus === "DELIVERED" || currentStatus === "CONCLUDED") {
    return "Entregue"
  }
  if (currentStatus === "PICKED_UP") {
    return order.type === "INDOOR" ? "Entregue" : "Retirado"
  }

  const formatHour = (date: Date) =>
    date.toLocaleTimeString("pt-BR", {
      hour: "2-digit",
      minute: "2-digit",
    })

  if (order.type === "DELIVERY") {
    const estimatedTime =
      order.deliveries?.[0]?.Delivery?.EstimatedDeliveryDateTime ||
      order.delivery?.estimatedDeliveryDateTime
    if (estimatedTime) return formatHour(new Date(estimatedTime))
  } else {
    const takeoutTime = order.OrderTakeouts?.[0]?.Takeout?.TakeoutDateTime
    if (takeoutTime) return formatHour(new Date(takeoutTime))
  }

  const createdEvent = order.events?.find((event) => event.status === "CREATED")
  if (createdEvent) {
    const createdDate = new Date(createdEvent.createdAt)
    const estimated = new Date(createdDate.getTime() + 60 * 60 * 1000)
    return formatHour(estimated)
  }

  return "--:--"
}

/**
 * Statuses at which the customer sees the door code: from the moment the store
 * confirms the order until the rider has it. The code is the customer's from the
 * start, so there is no reason to hide it while the kitchen works; showing it
 * early lets them find it calmly instead of hunting for it at the door.
 * Hidden before confirmation (the order may still be refused) and after
 * delivery (nothing left to confirm).
 */
const PICKUP_CODE_VISIBLE_STATUSES: ReadonlySet<OrderStatusType> = new Set<OrderStatusType>([
  "CONFIRMED",
  "PREPARING",
  "READY_FOR_PICKUP",
  "DISPATCHED",
  "PICKED_UP",
])

/**
 * The delivery pickup/confirmation code for this order, if the backend sent
 * one. Prefers the singular `delivery.pickupCode` (customer-service-gateway's
 * primary shape) and falls back to the legacy `deliveries[0].Delivery.PickupCode`
 * (PascalCase, older gateway shape), same precedence used for the delivery
 * address/ETA elsewhere in this file.
 */
export function getDeliveryPickupCode(
  order: OrderResponse | null | undefined,
): string | null {
  const code = order?.delivery?.pickupCode || order?.deliveries?.[0]?.Delivery?.PickupCode
  return code ? code : null
}

/**
 * True when the customer should be shown the pickup code prominently:
 * a DELIVERY order the store already confirmed, not yet delivered, and the
 * backend actually sent a code.
 */
export function shouldShowPickupCode(order: OrderResponse | null | undefined): boolean {
  if (!order || order.type !== "DELIVERY") return false
  const status = getCurrentStatus(order)
  if (!status || !PICKUP_CODE_VISIBLE_STATUSES.has(status)) return false
  return getDeliveryPickupCode(order) !== null
}

/**
 * Takeout time as "HH:mm": the scheduled takeout datetime, else the
 * CREATED event + 1h, else the order creation time. Extracted verbatim
 * from the order detail page.
 */
export function getTakeoutTime(order: OrderResponse): string {
  const takeoutTime = order.OrderTakeouts?.[0]?.Takeout?.TakeoutDateTime
  if (takeoutTime) return format(new Date(takeoutTime), "HH:mm")

  const createdEvent = order.events?.find((event) => event.status === "CREATED")
  if (createdEvent) {
    const createdDate = new Date(createdEvent.createdAt)
    return format(new Date(createdDate.getTime() + 60 * 60 * 1000), "HH:mm")
  }

  return format(order.createdAt, "HH:mm")
}

export type OrderProgressSummary = {
  status: OrderStatusType | null
  label: string
  progress: number
  progressTone: OrderProgressTone
  arrivalLabel: string
}

/**
 * Everything the progress UIs (active-order bar, order status card) derive
 * from an order. The arrival prefix copy differs between consumers, so it
 * stays local to each component.
 */
export function getOrderProgressSummary(order: OrderResponse): OrderProgressSummary {
  const status = getCurrentStatus(order)

  let label: string
  if (isAwaitingPixPayment(order)) {
    label = "Aguardando pagamento"
  } else if (status === "CANCELLED" && hasSettledRefund(order)) {
    label = `Pedido cancelado, estorno de ${formatBRL(getTotalRefundedAmount(order))} realizado`
  } else if (isScheduledOrder(order) && status !== null && SCHEDULED_WAITING_STATUSES.has(status)) {
    const readyAt = getScheduledReadyAt(order)
    label = readyAt ? formatScheduledFor(readyAt) : "Pedido agendado"
  } else {
    label = getStatusLabel(status, order.type)
  }

  return {
    status,
    label,
    progress: getProgressPercent(status),
    progressTone: getProgressTone(status),
    arrivalLabel: formatEstimatedArrival(order),
  }
}
